import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import './Outreach.css';
import OutreachEditor from './OutreachEditor';
import OutreachCandidates from './OutreachCandidates';
import OutreachAnalytics from './OutreachAnalytics';
import {
    getCompanyId,
    subscribeToCampaign,
    updateCampaign,
    saveWorkflowNodes,
    publishCampaign,
    pauseCampaign,
    resumeCampaign,
    updateLiveCampaign,
    getConnectedSenders,
    getGmailAuthUrl,
    getOutlookAuthUrl,
    disconnectSender
} from '../../services/outreachService';

const OutreachDraft = ({ campaign, onBack, initialTab }) => {
  useUiLanguage();
    const { t, i18n } = useTranslation('sourcing');
    const isRTL = i18n.dir() === 'rtl';

    // Firebase state
    const [companyId, setCompanyId] = useState(null);
    const [campaignData, setCampaignData] = useState(campaign);
    const [connectedSenders, setConnectedSenders] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);

    // Helper to calculate percentage and effective status
    const getCompletionPercentage = (stats) => {
        if (!stats) return 0;
        if (stats.completionPercentage !== undefined) return stats.completionPercentage;
        const total = stats.totalCandidates || stats.total || 0;
        const completed = stats.completed || 0;
        return total > 0 ? Math.round((completed / total) * 100) : 0;
    };

    const completionPercentage = getCompletionPercentage(campaignData?.stats);
    // Force completed status if 100%, regardless of database status (unless draft)
    const effectiveStatus = (completionPercentage === 100 && campaignData?.status !== 'draft') ? 'completed' : campaignData?.status;

    // Tabs for Active/Completed views
    const [activeDetailTab, setActiveDetailTab] = useState(initialTab || 'workflow');

    // Determine if we show the full tabbed view
    const isActiveOrCompleted = effectiveStatus === 'active' || effectiveStatus === 'completed' || effectiveStatus === 'paused';
    const isReadOnly = effectiveStatus === 'completed';

    // Use campaign nodes or default empty
    const [nodes, setNodes] = useState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [showTimerPopup, setShowTimerPopup] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const [transform, setTransform] = useState({ x: 0, y: 100, scale: 1 });
    const [isDragging, setIsDragging] = useState(false);
    const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    // Initialize auth and company ID
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const cid = await getCompanyId();
                    setCompanyId(cid);
                } catch (err) {
                    console.error('Error getting company ID:', err);
                }
            }
        });
        return () => unsubscribe();
    }, []);

    // Subscribe to campaign updates
    useEffect(() => {
        if (!companyId || !campaign?.id) return;

        const unsubscribe = subscribeToCampaign(companyId, campaign.id, (data) => {
            if (data) {
                setCampaignData(data);
                // Only update nodes if not currently editing
                if (!hasUnsavedChanges && data.nodes) {
                    setNodes(data.nodes);
                }
            }
        });

        return () => unsubscribe();
    }, [companyId, campaign?.id]);

    // Initialize nodes from campaign or create defaults
    useEffect(() => {
        if (campaignData?.nodes && campaignData.nodes.length > 0) {
            setNodes(campaignData.nodes);
        } else if (campaign?.id === 'new' || !campaignData?.nodes?.length) {
            // New campaign - start with empty or default nodes
            setNodes([]);
        }
    }, [campaignData?.id]);

    // Fetch connected senders
    useEffect(() => {
        if (!companyId) return;

        const fetchSenders = async () => {
            try {
                const senders = await getConnectedSenders(companyId);
                setConnectedSenders(senders);
            } catch (err) {
                console.error('Error fetching senders:', err);
            }
        };

        fetchSenders();
    }, [companyId]);

    // Set default tab for active/completed
    useEffect(() => {
        if (initialTab) {
            setActiveDetailTab(initialTab);
        }
    }, [initialTab]);

    const getWorkflowDimensions = () => {
        let width = 0;
        nodes.forEach((node, i) => {
            width += node.type === 'email' ? 90 : 45;
            if (i < nodes.length - 1) width += 100;
        });
        width += 100;
        return { width, height: 150 };
    };

    useLayoutEffect(() => {
        if (containerRef.current && activeDetailTab === 'workflow') {
            const { clientWidth, clientHeight } = containerRef.current;
            const { width } = getWorkflowDimensions();
            const effectiveWidth = width > 100 ? width : 200;
            const initialX = (clientWidth / 2) - (effectiveWidth / 2) + 50;
            const initialY = (clientHeight / 2) - 75;
            setTransform(prev => ({ ...prev, x: initialX, y: initialY }));
        }
    }, [nodes.length, activeDetailTab]);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                const { clientHeight } = containerRef.current;
                const targetY = (clientHeight / 2) - 100;
                setTransform(prev => ({ ...prev, y: targetY }));
            }
        };

        if (containerRef.current && activeDetailTab === 'workflow') {
            setTimeout(handleResize, 50);
            setTimeout(handleResize, 350);
        }
    }, [showEditor, activeDetailTab]);

    useEffect(() => {
        const handleWindowMouseMove = (e) => {
            if (isDragging) {
                const dx = e.clientX - lastMousePos.x;
                const dy = e.clientY - lastMousePos.y;
                setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
                setLastMousePos({ x: e.clientX, y: e.clientY });
            }
        };
        const handleWindowMouseUp = () => {
            if (isDragging) setIsDragging(false);
        };
        if (isDragging) {
            window.addEventListener('mousemove', handleWindowMouseMove);
            window.addEventListener('mouseup', handleWindowMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleWindowMouseMove);
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };
    }, [isDragging, lastMousePos]);

    const handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const scaleAmount = -e.deltaY * 0.001;
            const newScale = Math.min(Math.max(0.5, transform.scale + scaleAmount), 3);
            setTransform(prev => ({ ...prev, scale: newScale }));
        }
    };

    const handleMouseDown = (e) => {
        if (activeDetailTab !== 'workflow') return;
        setIsDragging(true);
        setLastMousePos({ x: e.clientX, y: e.clientY });
    };

    const handleNodeClick = (node, e) => {
        e.stopPropagation();
        if (isReadOnly) return;

        if (node.type === 'timer') {
            setShowEditor(false);
            setSelectedNode(node);
            setShowTimerPopup(true);
        } else if (node.type === 'email') {
            setShowTimerPopup(false);
            setSelectedNode(node);
            setShowEditor(true);
        }
    };

    const handleAddNodeClick = (e) => {
        e.stopPropagation();
        if (isReadOnly) return;
        setShowAddMenu(!showAddMenu);
    };

    const addNode = (type) => {
        const newId = Date.now();
        let newNode;
        if (type === 'email') {
            const emailCount = nodes.filter(n => n.type === 'email').length;
            // Inherit sender from the first email step
            const firstEmailNode = nodes.find(n => n.type === 'email');
            const inheritedFrom = firstEmailNode?.from || '';

            newNode = {
                id: newId,
                type: 'email',
                title: t('outreach.draft.emailStepTitle', { count: emailCount + 1 }),
                subtitle: t('outreach.draft.emailStepSubtitle'),
                subject: '',
                body: '',
                from: inheritedFrom,
                cc: '',
                bcc: ''
            };
        } else {
            newNode = {
                id: newId,
                type: 'timer',
                duration: `2 ${t('outreach.draft.days')}`,
                durationDays: 2
            };
        }
        const updatedNodes = [...nodes, newNode];
        setNodes(updatedNodes);
        setShowAddMenu(false);
        setHasUnsavedChanges(true);
    };

    const deleteNode = (nodeId) => {
        if (window.confirm(t('outreach.draft.deleteStepMessage'))) {
            const updatedNodes = nodes.filter(n => n.id !== nodeId);
            setNodes(updatedNodes);
            setShowEditor(false);
            setShowTimerPopup(false);
            setSelectedNode(null);
            setHasUnsavedChanges(true);
        }
    };

    const updateNode = (updatedNode) => {
        let finalNodes = nodes.map(n => n.id === updatedNode.id ? updatedNode : n);

        // Auto-propagate sender to all email nodes if this is the first email node
        if (updatedNode.type === 'email' && updatedNode.from) {
            const firstEmailNode = nodes.find(n => n.type === 'email');
            if (firstEmailNode?.id === updatedNode.id) {
                finalNodes = finalNodes.map(n => {
                    if (n.type === 'email' && n.id !== updatedNode.id) {
                        return { ...n, from: updatedNode.from };
                    }
                    return n;
                });
            }
        }

        setNodes(finalNodes);
        setHasUnsavedChanges(true);

        // Update selectedNode if it's part of the change
        const updatedSelectedNode = finalNodes.find(n => n.id === selectedNode?.id);
        if (updatedSelectedNode) {
            setSelectedNode(updatedSelectedNode);
        }
    };

    /**
     * Prepares nodes for saving/publishing by:
     * 1. Validating that a sender is selected in Step 1
     * 2. Propagating the Step 1 sender to all follow-ups
     * 3. Propagating the Step 1 subject to all follow-ups (if missing)
     */
    const getPreparedNodes = () => {
        const firstEmailNode = nodes.find(n => n.type === 'email');
        if (firstEmailNode && !firstEmailNode.from) {
            alert(t('outreach.errors.noSenderSelected', 'Please select a sender email in Step 1 before saving.'));
            return null;
        }

        const senderEmail = firstEmailNode?.from;
        const firstSubject = firstEmailNode?.subject;
        let isFirstEmail = true;

        return nodes.map(n => {
            if (n.type === 'email') {
                const updates = { ...n };
                if (senderEmail) {
                    updates.from = senderEmail;
                }
                // For follow-up emails, inherit subject from Step 1
                if (!isFirstEmail && firstSubject && !n.subject) {
                    updates.subject = firstSubject;
                }
                isFirstEmail = false;
                return updates;
            }
            return n;
        });
    };

    // Save workflow to Firebase
    const handleSave = async () => {
        if (!companyId || !campaignData?.id) return;

        const nodesToSave = getPreparedNodes();
        if (!nodesToSave) return;

        setIsSaving(true);
        setSaveMessage(null);
        try {
            await saveWorkflowNodes(companyId, campaignData.id, nodesToSave);
            setNodes(nodesToSave); // Update local state with propagated senders/subjects
            setHasUnsavedChanges(false);
            setSaveMessage({ type: 'success', text: t('outreach.messages.saved', 'Saved successfully!') });
            setTimeout(() => setSaveMessage(null), 3000);
            return true; // Return success for handlePublish to use
        } catch (err) {
            console.error('Error saving workflow:', err);
            setSaveMessage({ type: 'error', text: t('outreach.messages.saveError', 'Failed to save') });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    // Publish campaign
    const handlePublish = async () => {
        if (!companyId || !campaignData?.id) return;

        // 1. Prepare nodes (validates sender & propagates info)
        const nodesToSave = getPreparedNodes();
        if (!nodesToSave) return;

        // 2. Validate content before publishing
        if (nodesToSave.length === 0) {
            alert(t('outreach.errors.noSteps', 'Please add at least one email step before publishing.'));
            return;
        }

        const emailNodes = nodesToSave.filter(n => n.type === 'email');

        // Helper to check if body is truly empty (Quill can have <p><br></p> for empty)
        const isBodyEmpty = (body) => {
            if (!body) return true;
            const stripped = body.replace(/<[^>]*>/g, '').trim();
            return stripped.length === 0;
        };

        const incompleteEmails = emailNodes.filter(n => !n.subject || isBodyEmpty(n.body));
        if (incompleteEmails.length > 0) {
            console.log('Incomplete emails:', incompleteEmails.map(n => ({ id: n.id, title: n.title, subject: n.subject, body: n.body?.substring(0, 50) })));
            alert(t('outreach.errors.incompleteEmails', 'Please complete all email steps before publishing.') +
                `\n\nMissing: ${incompleteEmails.map(n => n.title || 'Email').join(', ')}`);
            return;
        }

        if (!window.confirm(t('outreach.confirmPublish', 'Are you sure you want to publish this campaign? Emails will start being scheduled.'))) {
            return;
        }

        setIsPublishing(true);
        try {
            // 3. Save first to ensure latest content & propagation
            await saveWorkflowNodes(companyId, campaignData.id, nodesToSave);
            setNodes(nodesToSave);
            setHasUnsavedChanges(false);

            // 4. Then Publish
            await publishCampaign(companyId, campaignData.id);
            setSaveMessage({ type: 'success', text: t('outreach.messages.published', 'Campaign published!') });
            // Wait a moment for Firebase to update, then go back
            setTimeout(() => onBack(), 1500);
        } catch (err) {
            console.error('Error publishing:', err);
            alert(t('outreach.errors.publishFailed', 'Failed to publish campaign. Please try again.'));
        } finally {
            setIsPublishing(false);
        }
    };

    // Pause campaign
    const handlePause = async () => {
        if (!companyId || !campaignData?.id) return;

        try {
            await pauseCampaign(companyId, campaignData.id);
            setSaveMessage({ type: 'success', text: t('outreach.messages.paused', 'Campaign paused') });
        } catch (err) {
            console.error('Error pausing:', err);
            alert(t('outreach.errors.pauseFailed', 'Failed to pause campaign'));
        }
    };

    // Resume campaign
    const handleResume = async () => {
        if (!companyId || !campaignData?.id) return;

        try {
            await resumeCampaign(companyId, campaignData.id);
            setSaveMessage({ type: 'success', text: t('outreach.messages.resumed', 'Campaign resumed') });
        } catch (err) {
            console.error('Error resuming:', err);
            alert(t('outreach.errors.resumeFailed', 'Failed to resume campaign'));
        }
    };

    // Update live campaign (changes content but not schedule)
    const handleUpdate = async () => {
        if (!companyId || !campaignData?.id) return;

        const nodesToSave = getPreparedNodes();
        if (!nodesToSave) return;

        setIsSaving(true);
        try {
            await saveWorkflowNodes(companyId, campaignData.id, nodesToSave);
            await updateLiveCampaign(companyId, campaignData.id, { nodes: nodesToSave });
            setNodes(nodesToSave);
            setHasUnsavedChanges(false);
            setSaveMessage({ type: 'success', text: t('outreach.messages.updated', 'Campaign updated! Scheduled emails will use new content.') });
            setTimeout(() => setSaveMessage(null), 4000);
        } catch (err) {
            console.error('Error updating:', err);
            alert(t('outreach.errors.updateFailed', 'Failed to update campaign'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveEditor = () => {
        // Editor saves are handled via updateNode
        setHasUnsavedChanges(true);
    };

    const handleCloseEditor = () => {
        setShowEditor(false);
        setSelectedNode(null);
    };

    const updateTimerDuration = (days) => {
        if (selectedNode) {
            const updatedNode = {
                ...selectedNode,
                duration: `${days} ${t('outreach.draft.days')}`,
                durationDays: days
            };
            updateNode(updatedNode);
            setSelectedNode(updatedNode);
        }
    };

    // Helper to render content based on active tab
    const renderContent = () => {
        if (activeDetailTab === 'candidates') {
            return <OutreachCandidates campaign={campaignData} isDraft={campaignData?.status === 'draft'} companyId={companyId} />;
        } else if (activeDetailTab === 'analytics') {
            return <OutreachAnalytics campaign={campaignData} />;
        } else {
            // Workflow View (Default)
            return (
                <div
                    className="draft-canvas-container"
                    ref={containerRef}
                    style={{
                        flex: showEditor ? '0 0 250px' : '1',
                        minHeight: showEditor ? '200px' : '0',
                        background: 'var(--app-surface, rgba(255,255,255,0.02))',
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: (isDragging && !isReadOnly) ? 'grabbing' : (!isReadOnly ? 'grab' : 'default'),
                        transition: 'flex 0.3s ease, min-height 0.3s ease',
                        width: '100%',
                        height: '100%'
                    }}
                    onMouseDown={handleMouseDown}
                    onWheel={handleWheel}
                >
                    <div
                        className="canvas-content"
                        style={{
                            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                            transformOrigin: '0 0',
                            display: 'flex',
                            alignItems: 'center',
                            position: 'absolute',
                            padding: '100px 50px',
                            transition: isDragging ? 'none' : 'transform 0.25s ease'
                        }}
                    >
                        {nodes.length === 0 && !isReadOnly && (
                            <div style={{
                                textAlign: 'center',
                                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)',
                                padding: '40px'
                            }}>
                                <i className="fa-solid fa-plus-circle" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}></i>
                                <p>{t('outreach.draft.addFirstStep', 'Add your first email or timer step')}</p>
                            </div>
                        )}

                        {nodes.map((node, index) => (
                            <div key={node.id} style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                                <div
                                    className={`seq-node ${node.type} ${selectedNode?.id === node.id ? 'selected' : ''}`}
                                    onClick={(e) => handleNodeClick(node, e)}
                                    style={{ position: 'relative', cursor: isReadOnly ? 'default' : 'pointer' }}
                                >
                                    <div className="seq-node-title">
                                        {node.type === 'email' ? node.title : t('outreach.draft.waitDuration', { duration: node.duration || `${node.durationDays || 2} ${t('outreach.draft.days')}` })}
                                    </div>
                                    <div className={`seq-node-box ${selectedNode?.id === node.id ? 'selected' : ''}`}>
                                        <i className={node.type === 'email' ? 'fa-solid fa-envelope' : 'fa-regular fa-clock'}></i>
                                    </div>

                                    {/* Timer Popup - Only show if not read only */}
                                    {!isReadOnly && showTimerPopup && selectedNode?.id === node.id && node.type === 'timer' && (
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                position: 'absolute',
                                                top: 'calc(100% + 10px)',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                background: 'var(--app-surface, #0a1930)',
                                                border: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))",
                                                borderRadius: '12px',
                                                padding: '20px',
                                                width: '200px',
                                                zIndex: 50,
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                                            }}
                                         >
                                            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '16px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.8)', textAlign: 'center' }}>{t('outreach.draft.waitDurationLabel')}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '20px' }}>
                                                <button
                                                    onClick={() => updateTimerDuration(Math.max(1, (node.durationDays || parseInt(node.duration)) - 1))}
                                                    style={{ background: 'var(--app-surface, rgba(86,141,237,0.15))', border: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))", color: '#568ded', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                  data-light-control={"quiet"}>−</button>
                                                <div style={{ textAlign: 'center' }}>
                                                    <span style={{ fontWeight: "var(--app-weight-strong, 700)", fontSize: '28px', color: 'var(--app-text, white)' }}>{node.durationDays || parseInt(node.duration) || 2}</span>
                                                    <div style={{ fontSize: '11px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)', marginTop: '2px' }}>{t('outreach.draft.days')}</div>
                                                </div>
                                                <button
                                                    onClick={() => updateTimerDuration((node.durationDays || parseInt(node.duration) || 2) + 1)}
                                                    style={{ background: 'var(--app-surface, rgba(86,141,237,0.15))', border: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))", color: '#568ded', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                  data-light-control={"quiet"}>+</button>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => deleteNode(node.id)}
                                                    style={{ flex: 1, padding: '10px', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', color: 'var(--app-tone-e74c3c, #e74c3c)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                                  data-light-control={"quiet-danger"}><i className="fa-regular fa-trash-can" style={{ color: 'var(--app-tone-e74c3c, #e74c3c)' }}></i> {t('outreach.editor.delete')}</button>
                                                <button
                                                    onClick={() => setShowTimerPopup(false)}
                                                    style={{ '--light-control-fill': '#568ded', flex: 1, padding: '10px', background: '#568ded', border: 'none', color: 'white', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                                                  data-light-control={"filled"}>{t('outreach.buttons.done')}</button>
                                            </div>
                                            <div style={{ position: 'absolute', top: '-7px', left: '50%', transform: 'translateX(-50%) rotate(45deg)', width: '12px', height: '12px', background: 'var(--app-surface, #0a1930)', borderLeft: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))", borderTop: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))" }}></div>
                                        </div>
                                    )}
                                </div>

                                {index < nodes.length - 1 && (
                                    <div className="seq-connection">
                                        <div className="seq-connection-dot" style={{ position: 'absolute', [isRTL ? 'right' : 'left']: '-3px' }}></div>
                                        <div className="seq-connection-dot" style={{ position: 'absolute', [isRTL ? 'left' : 'right']: '-3px' }}></div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Add Button - Only if not read only */}
                        {!isReadOnly && (
                            <div className="seq-connection" style={{ width: '60px', position: 'relative' }}>
                                {nodes.length > 0 && <div className="seq-connection-dot" style={{ position: 'absolute', [isRTL ? 'right' : 'left']: '-3px' }}></div>}
                                <div
                                    onClick={handleAddNodeClick}
                                    style={{ position: 'absolute', [isRTL ? 'left' : 'right']: '-15px', top: '-15px', width: '30px', height: '30px', borderRadius: '50%', background: '#568ded', display: 'grid', placeItems: 'center', cursor: 'pointer', color: 'white', zIndex: 5, padding: 0 }}
                                 >
                                    <i className={`fa-solid ${showAddMenu ? 'fa-xmark' : 'fa-plus'}`} style={{ fontSize: '14px' }}></i>
                                </div>

                                {showAddMenu && (
                                    <div
                                        style={{
                                            position: 'absolute', top: '25px', left: '25px',
                                            background: 'var(--app-surface, #0a1930)',
                                            border: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))",
                                            borderRadius: '12px',
                                            padding: '12px',
                                            display: 'flex', flexDirection: 'column', gap: '4px',
                                            width: '140px',
                                            zIndex: 100,
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                                        }}
                                    >
                                        <button
                                            onClick={() => addNode('email')}
                                            style={{ background: 'transparent', border: 'none', color: 'var(--app-text, white)', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', padding: '10px 8px', borderRadius: '8px' }}
                                          data-light-control={"quiet"}>
                                            <i className="fa-solid fa-envelope" style={{ color: '#568ded', width: '16px' }}></i> {t('outreach.draft.addEmail')}
                                        </button>
                                        <button
                                            onClick={() => addNode('timer')}
                                            style={{ background: 'transparent', border: 'none', color: 'var(--app-text, white)', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', padding: '10px 8px', borderRadius: '8px' }}
                                          data-light-control={"quiet"}>
                                            <i className="fa-regular fa-clock" style={{ color: '#568ded', width: '16px' }}></i> {t('outreach.draft.addTimer')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )
        }
    };

    const translateDate = (dateStr) => {
        if (!dateStr) return '';
        if (typeof dateStr === 'number') {
            const date = new Date(dateStr);
            const month = date.toLocaleString('default', { month: 'long' });
            const year = date.getFullYear();
            return `${t(`outreach.months.${month.toLowerCase()}`, month)} ${year}`;
        }
        const parts = dateStr.split(' ');
        if (parts.length !== 2) return dateStr;
        const [month, year] = parts;
        const monthKey = month.toLowerCase();
        return `${t(`outreach.months.${monthKey}`, month)} ${year}`;
    };

    return (
        <div className="outreach-draft-view" style={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="outreach-header-actions" style={{ padding: '0 0 20px 0', borderBottom: "1px solid var(--app-border, rgba(255,255,255,0.1))", zIndex: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h2 className="outreach-subtitle" style={{ fontSize: '24px', fontWeight: "var(--app-weight-strong, 800)", margin: 0 }}>
                                {t('outreach.titlePrefix', 'Outreach')} - {campaignData?.name || uiText("New Campaign")}
                            </h2>
                            <span className={`status-badge ${effectiveStatus || 'draft'}`}>
                                <span className="dot"></span>
                                {t(`outreach.tabs.${effectiveStatus === 'paused' ? 'active' : effectiveStatus || 'draft'}`)}
                                {effectiveStatus === 'paused' && ` (${t('outreach.status.paused', 'Paused')})`}
                            </span>
                            {saveMessage && (
                                <span style={{
                                    fontSize: '13px',
                                    color: saveMessage.type === 'success' ? 'var(--app-tone-2ecc71, #2ecc71)' : 'var(--app-tone-e74c3c, #e74c3c)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <i className={saveMessage.type === 'success' ? 'fa-solid fa-check' : 'fa-solid fa-exclamation'}></i>
                                    {saveMessage.text}
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: '13px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)', marginTop: '2px' }}>
                            {t('outreach.stats.steps', { count: nodes.length })} &nbsp; <span style={{ opacity: 0.5 }}>|</span> &nbsp; {translateDate(campaignData?.createdAt || campaignData?.date)}
                        </div>

                        {/* Tabs for Draft/Active/Completed */}
                        {(isActiveOrCompleted || effectiveStatus === 'draft') && (
                            <div className="detail-tabs">
                                <div
                                    className={`detail-tab ${activeDetailTab === 'workflow' ? 'active' : ''}`}
                                    onClick={() => setActiveDetailTab('workflow')}
                                >
                                    {t('outreach.tabs.workflow', 'Workflow')}
                                </div>
                                <div
                                    className={`detail-tab ${activeDetailTab === 'candidates' ? 'active' : ''}`}
                                    onClick={() => setActiveDetailTab('candidates')}
                                >
                                    {t('outreach.tabs.candidates', 'Candidates')}
                                </div>
                                {isActiveOrCompleted && (
                                    <div
                                        className={`detail-tab ${activeDetailTab === 'analytics' ? 'active' : ''}`}
                                        onClick={() => setActiveDetailTab('analytics')}
                                    >
                                        {t('outreach.tabs.analytics', 'Analytics')}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Actions - Dynamic based on campaign status */}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {/* Completed: Close only */}
                        {effectiveStatus === 'completed' && (
                            <button
                                onClick={onBack}
                                style={{ background: 'transparent', border: "1px solid var(--app-border, rgba(255,255,255,0.2))", color: 'var(--app-text, white)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Montserrat', fontSize: '14px' }}
                              data-light-control={"quiet"}>{t('outreach.buttons.close')}</button>
                        )}

                        {/* Draft: Close, Save, Publish */}
                        {effectiveStatus === 'draft' && (
                            <>
                                <button
                                    onClick={() => { if (hasUnsavedChanges && !window.confirm(t('outreach.draft.unsavedChangesConfirm'))) return; onBack(); }}
                                    style={{ background: 'transparent', border: "1px solid var(--app-border, rgba(255,255,255,0.2))", color: 'var(--app-text, white)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Montserrat', fontSize: '14px' }}
                                  data-light-control={"quiet"}>{t('outreach.buttons.close')}</button>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    style={{ background: 'transparent', border: '1px solid #568ded', color: '#568ded', padding: '8px 16px', borderRadius: '6px', cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}
                                  data-light-control={"quiet"}>
                                    {isSaving && <i className="fa-solid fa-spinner fa-spin"></i>}
                                    {t('outreach.buttons.save')}
                                </button>
                                <button
                                    onClick={handlePublish}
                                    disabled={isPublishing || nodes.length === 0}
                                    style={{ '--light-control-fill': nodes.length === 0 ? '#666' : '#27ae60', background: nodes.length === 0 ? '#666' : '#27ae60', border: 'none', color: nodes.length === 0 ? 'var(--app-text, white)' : "#FFFFFF", padding: '8px 16px', borderRadius: '6px', cursor: (isPublishing || nodes.length === 0) ? 'not-allowed' : 'pointer', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}
                                  data-light-control={nodes.length === 0 ? "filled" : "filled"}>
                                    {isPublishing && <i className="fa-solid fa-spinner fa-spin"></i>}
                                    {t('outreach.buttons.publish')}
                                </button>
                            </>
                        )}

                        {/* Active: Close, Update, Pause */}
                        {effectiveStatus === 'active' && (
                            <>
                                <button
                                    onClick={() => { if (hasUnsavedChanges && !window.confirm(t('outreach.draft.unsavedChangesConfirm'))) return; onBack(); }}
                                    style={{ background: 'transparent', border: "1px solid var(--app-border, rgba(255,255,255,0.2))", color: 'var(--app-text, white)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Montserrat', fontSize: '14px' }}
                                  data-light-control={"quiet"}>{t('outreach.buttons.close')}</button>
                                <button
                                    onClick={handleUpdate}
                                    disabled={isSaving}
                                    style={{ '--light-control-fill': '#568ded', background: '#568ded', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '6px', cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}
                                  data-light-control={"filled"}>
                                    {isSaving && <i className="fa-solid fa-spinner fa-spin"></i>}
                                    {t('outreach.buttons.update')}
                                </button>
                                <button
                                    onClick={handlePause}
                                    style={{ '--light-control-fill': '#f39c12', background: '#f39c12', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600 }}
                                  data-light-control={"filled"}>{t('outreach.buttons.pause')}</button>
                            </>
                        )}

                        {/* Paused: Close, Update, Resume */}
                        {effectiveStatus === 'paused' && (
                            <>
                                <button
                                    onClick={() => { if (hasUnsavedChanges && !window.confirm(t('outreach.draft.unsavedChangesConfirm'))) return; onBack(); }}
                                    style={{ background: 'transparent', border: "1px solid var(--app-border, rgba(255,255,255,0.2))", color: 'var(--app-text, white)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Montserrat', fontSize: '14px' }}
                                  data-light-control={"quiet"}>{t('outreach.buttons.close')}</button>
                                <button
                                    onClick={handleUpdate}
                                    disabled={isSaving}
                                    style={{ '--light-control-fill': '#568ded', background: '#568ded', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '6px', cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}
                                  data-light-control={"filled"}>
                                    {isSaving && <i className="fa-solid fa-spinner fa-spin"></i>}
                                    {t('outreach.buttons.update')}
                                </button>
                                <button
                                    onClick={handleResume}
                                    style={{ '--light-control-fill': '#27ae60', background: '#27ae60', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600 }}
                                  data-light-control={"filled"}>{t('outreach.buttons.resume')}</button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {renderContent()}

                {/* Email Editor Panel - Only visible in Workflow view and when editor is open */}
                {activeDetailTab === 'workflow' && showEditor && selectedNode && selectedNode.type === 'email' && (
                    <div
                        className="outreach-editor-panel"
                        style={{
                            flex: '1',
                            background: 'var(--app-background, #031128)',
                            borderTop: "1px solid var(--app-border, rgba(86,141,237,0.2))",
                            overflow: 'auto',
                            padding: '20px',
                            animation: 'slideUp 0.3s ease',
                            boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
                            zIndex: 20,
                        }}
                    >
                        <OutreachEditor
                            key={selectedNode.id}
                            node={selectedNode}
                            onBack={handleCloseEditor}
                            onSave={handleSaveEditor}
                            onDelete={() => deleteNode(selectedNode.id)}
                            setHasUnsavedChanges={setHasUnsavedChanges}
                            campaign={campaignData}
                            isInline={true}
                            connectedSenders={connectedSenders}
                            onNodeUpdate={updateNode}
                            // New Props for Sender Management
                            onConnectSender={async (provider) => {
                                try {
                                    const url = provider === 'google'
                                        ? await getGmailAuthUrl(companyId)
                                        : await getOutlookAuthUrl(companyId);
                                    window.location.href = url;
                                } catch (err) {
                                    console.error("Connect error:", err);
                                    alert(t('outreach.errors.connectFailed', 'Failed to initiate connection'));
                                }
                            }}
                            onDisconnectSender={async (senderId) => {
                                if (window.confirm(t('outreach.prompts.disconnectSender', 'Disconnect this email account?'))) {
                                    try {
                                        await disconnectSender(companyId, senderId);
                                    } catch (err) {
                                        console.error("Disconnect error:", err);
                                    }
                                }
                            }}
                            // Thread logic
                            isThreadReply={nodes.findIndex(n => n.type === 'email' && n.id === selectedNode.id) > 0}
                            threadSubject={nodes.find(n => n.type === 'email')?.subject || ''}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default OutreachDraft;
